import FitParser from 'fit-file-parser';

export interface TelemetryData {
  timestamps: number[]; // Offset in seconds
  heart_rate: number[];
  power: number[];
  cadence: number[];
  speed: number[];
  altitude: number[];
  gps_lat: number[];
  gps_lng: number[];
}

export interface ParsedFitResult {
  startTime: Date;
  totalDuration: number;
  totalDistance: number;
  avgHeartRate: number;
  normalizedPower: number | null;
  sport: string;
  telemetry: TelemetryData;
}

export async function parseFitFile(buffer: ArrayBuffer): Promise<ParsedFitResult> {
  return new Promise((resolve, reject) => {
    const parser = new FitParser({
      force: true,
      speedUnit: 'm/s',
      lengthUnit: 'm',
      temperatureUnit: 'celsius',
      elapsedRecordField: true,
      mode: 'list',
    });

    parser.parse(buffer, (error: string | null | undefined, data: any) => {
      if (error) {
        reject(error);
        return;
      }

      // Extract aggregate metrics
      const session = data.sessions && data.sessions.length > 0 ? data.sessions[0] : {};
      const startTime = session.start_time ? new Date(session.start_time) : new Date();
      const totalDuration = session.total_elapsed_time || 0;
      const totalDistance = session.total_distance || 0;
      const avgHeartRate = session.avg_heart_rate || 0;
      const normalizedPower = session.normalized_power || null;
      const sport = session.sport ? String(session.sport).toLowerCase() : 'unknown';

      // Extract high frequency telemetry streams
      const records = data.records || [];
      const telemetry: TelemetryData = {
        timestamps: [],
        heart_rate: [],
        power: [],
        cadence: [],
        speed: [],
        altitude: [],
        gps_lat: [],
        gps_lng: [],
      };

      let lastDist = 0;
      let lastTime = -1;

      records.forEach((record: any) => {
        if (record.elapsed_time !== undefined) {
          telemetry.timestamps.push(record.elapsed_time);
          telemetry.heart_rate.push(record.heart_rate || 0);
          telemetry.power.push(record.power || 0);
          telemetry.cadence.push(record.cadence || 0);
          
          let spd = record.enhanced_speed !== undefined ? record.enhanced_speed : (record.speed || 0);
          
          // Fallback: derive speed from distance if native speed is 0 or missing
          if (!spd && record.distance !== undefined && lastTime >= 0) {
            const dt = record.elapsed_time - lastTime;
            const dd = record.distance - lastDist;
            if (dt > 0 && dd >= 0) spd = dd / dt;
          }
          if (record.distance !== undefined) lastDist = record.distance;
          lastTime = record.elapsed_time;

          telemetry.speed.push(spd);
          telemetry.altitude.push(record.enhanced_altitude !== undefined ? record.enhanced_altitude : (record.altitude || 0));
          telemetry.gps_lat.push(record.position_lat || 0);
          telemetry.gps_lng.push(record.position_long || 0);
        }
      });

      // ── Physics-based Power Estimation for Cycling ──
      const isBike = sport.includes('cycl') || sport.includes('bik');
      const hasPower = telemetry.power.some(p => p > 0);
      let finalNormalizedPower = normalizedPower;
      
      if (isBike && !hasPower && telemetry.speed.length > 0) {
        const m = 97.4; // 87.9kg rider + ~9.5kg CAAD8
        const g = 9.81;
        const Crr = 0.0058; // Continental Gatorskin 700c
        const CdA = 0.38; // Standard road posture
        const rho = 1.2;
        
        let sumPower = 0;
        let countPower = 0;

        // Apply a slight low-pass filter to altitude to smooth out grade spikes
        const smoothedAlt = [...telemetry.altitude];
        for (let i = 2; i < smoothedAlt.length - 2; i++) {
          smoothedAlt[i] = (telemetry.altitude[i-2] + telemetry.altitude[i-1] + telemetry.altitude[i] + telemetry.altitude[i+1] + telemetry.altitude[i+2]) / 5;
        }

        for (let i = 0; i < telemetry.timestamps.length; i++) {
          const v = telemetry.speed[i]; // m/s
          if (v < 1) {
            telemetry.power[i] = 0;
            continue;
          }

          let grade = 0;
          if (i > 0) {
            const dt = telemetry.timestamps[i] - telemetry.timestamps[i - 1];
            const dAlt = smoothedAlt[i] - smoothedAlt[i - 1];
            if (dt > 0 && Math.abs(dAlt) < 5) {
              const dist = v * dt;
              if (dist > 0) {
                grade = dAlt / dist;
              }
            }
          }

          // Clamp grade to sensible limits (-20% to +20%)
          grade = Math.max(-0.2, Math.min(0.2, grade));

          const F_roll = Crr * m * g;
          const F_grav = m * g * grade;
          const F_aero = 0.5 * rho * CdA * v * v;
          
          const pwr = (F_roll + F_grav + F_aero) * v;
          let pwrRounded = pwr > 0 ? Math.round(pwr) : 0;
          
          // Coasting heuristic & artifact capping
          if (grade < -0.02 && v > 11) {
            pwrRounded = 0; // Likely coasting > 40kph downhill
          } else if (pwrRounded > 1000) {
            pwrRounded = 1000; // Cap noisy GPS altimeter spikes
          }
          
          // Drivetrain loss ~3%
          telemetry.power[i] = pwrRounded > 0 ? Math.round(pwrRounded / 0.97) : 0;
          
          if (telemetry.power[i] > 0) {
            sumPower += telemetry.power[i];
            countPower++;
          }
        }
        
        // Calculate Normalized Power (30s rolling avg ^ 4)
        if (countPower > 30) {
          let sum4 = 0;
          let validVals = 0;
          for (let i = 29; i < telemetry.power.length; i++) {
            let rollSum = 0;
            for (let j = 0; j < 30; j++) rollSum += telemetry.power[i - j];
            const rollAvg = rollSum / 30;
            sum4 += Math.pow(rollAvg, 4);
            validVals++;
          }
          if (validVals > 0) {
            finalNormalizedPower = Math.round(Math.pow(sum4 / validVals, 0.25));
          }
        } else if (countPower > 0) {
          finalNormalizedPower = Math.round(sumPower / countPower);
        }
      }

      resolve({
        startTime,
        totalDuration,
        totalDistance,
        avgHeartRate,
        normalizedPower: finalNormalizedPower,
        sport,
        telemetry
      });
    });
  });
}
